import React, { useState, useEffect, useRef } from 'react';
import Tooltip from '../ToolTip';

const GenericPropertiesControl = ({ 
  properties, 
  onPropertyUpdate, 
  title = "EIGENSCHAFTEN" 
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [tempValues, setTempValues] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);

  // Watch for property changes
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 1000);
    return () => clearTimeout(timer);
  }, [properties]);

  const startEdit = (key, currentValue) => {
    setEditingField(key);
    setTempValues({ ...tempValues, [key]: currentValue });
  };

  const saveEdit = (key) => {
    if (onPropertyUpdate) {
      onPropertyUpdate(key, tempValues[key]);
    }
    setEditingField(null);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setTempValues({});
  };

  const handleInputChange = (key, value) => {
    setTempValues({ ...tempValues, [key]: value });
  };

  const handleKeyPress = (e, key) => {
    if (e.key === 'Enter') {
      saveEdit(key);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Handle dropdown change
  const handleDropdownChange = (key, value, isObjectOption = false) => {
    if (onPropertyUpdate) {
      onPropertyUpdate(key, value);
    }
  };

 const formatDisplayValue = (value) => {
  // Handle null or undefined
  if (value === null || value === undefined) {
    return "";
  }
  
  // Check if it's a React element (has $$typeof symbol)
  if (typeof value === 'object' && value.$$typeof) {
    console.warn("React element passed as value, skipping formatting");
    return "";
  }
  
  // Handle objects (but not arrays)
  if (typeof value === 'object' && !Array.isArray(value)) {
    try {
      // Filter out symbol properties
      const entries = Object.entries(value).filter(([key]) => typeof key === 'string');
      return entries
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
    } catch (error) {
      console.error("Error formatting object value:", error, value);
      return "";
    }
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  
  // Handle strings (including URLs) and other primitives
  return String(value);
};
    const isURL = (str) => {
    if (!str || typeof str !== 'string') return false;
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }

  };

  // Render input field based on property type
  const renderInputField = (property) => {
    if (property.objectOptions ) {
      const options = property.objectOptions;
      if (!options || options.length === 0) return null; 
        const selectedIndex = options.findIndex((opt) => {
      if (!property.value) return false;
        return Object.entries(opt.value).every(([k, v]) => property.value[k] === v);
      });

      const selectValue = selectedIndex >= 0 ? String(selectedIndex) : '';
      
      return (
        <select
          value={selectValue}
          onChange={(e) => {
          const idx = e.target.value;
          const chosen = idx !== '' ? options[idx].value : null;
          handleDropdownChange(property.key, chosen);
          }}
          style={{
            fontSize: '13px',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '4px',
            padding: '4px 8px',
            width: '240px',
            outline: 'none',
            backgroundColor: '#FFFFFF',
            cursor: 'pointer'
          }}
        >

          <option value="">--  --</option>
          {options.map((option, index) => (
            <option key={index} value={String(index)}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }
    
    // Handle simple dropdown
    if (property.options && property.options.length > 0) {
      return (
        <select
          value={property.value || ''}
          onChange={(e) => handleDropdownChange(property.key, e.target.value)}
          style={{
            fontSize: '13px',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '4px',
            padding: '3px 8px',
            width: '100%',
            outline: 'none',
            backgroundColor: '#FFFFFF',
            cursor: 'pointer'
          }}
        >
          <option value="">--  --</option>
          {property.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    } 
    
    // Handle text input editing
    if (editingField === property.key) {
      return (
        <input
          type="text"
          value={tempValues[property.key] || ''}
          onChange={(e) => handleInputChange(property.key, e.target.value)}
          onKeyDown={(e) => handleKeyPress(e, property.key)}
          autoFocus
          style={{
            fontSize: '13px',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '4px',
            padding: '4px 8px',
            width: '120px',
            outline: 'none'
          }}
        />
      );
    } 
    
    // Display value
    return (
      <span style={{
        fontSize: '13px',
        color: '#374151',
        minWidth: '120px',
        textAlign: 'right'
      }}>
        {isURL(property.value) ? (
      <a 
        href={property.value} 
        target="_blank" 
        rel="noopener noreferrer"
        style={{
          color: '#3B82F6',
          textDecoration: 'underline'
        }}
      >
        {property.value}
      </a>
    ) : (
      formatDisplayValue(property.value)
    )}
      </span>
    );
  };

  return (
    <div style={{
      width: '100%',
      backgroundColor: '#FFFFFF',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
      height: '100%',        
      display: 'flex',      
      flexDirection: 'column' 
    }}>
      {/* Header */}
      <div style={{
        padding: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: 'column',
        gap: '8px'
      }}>
        <h3 style={{
          fontSize: '11px',
          fontWeight: 500,
          color: '#808489',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          font: "GoldbeckNextWebHeadline2",
          margin: 0,
          width: '100%'
        }}>
          {title}
        </h3>
        
        {/* Animated Glow Line */}
        <div style={{
          width: '100%',
          height: '4px',
          backgroundColor: '#E5E7EB',
          borderRadius: '1px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            background: 'linear-gradient(90deg, transparent 0%, #3B82F6 50%, transparent 100%)',
            transform: isAnimating ? 'translateX(0%)' : 'translateX(-100%)',
            transition: isAnimating ? 'transform 1s ease-out' : 'none',
            boxShadow: '0 0 8px rgba(59, 130, 246, 0.6)'
          }} />
        </div>
      </div>

      {/* Properties List */}
      {!collapsed && (
        <div style={{
          padding: '0 8px 8px 8px',
          flex:1,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
        }}>
          {properties.map((property) => (
            <div 
              key={property.key} 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 1px',
                borderBottom: '1px solid #F3F4F6',
                transition: 'background-color 0.15s ease-in-out'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {/* Property Label */}
              <div style={{
                fontSize: '13px',
                color: '#27292B',
                fontWeight: '400',
                font: "GoldbeckNextWebHeadline2",
                minWidth: '80px',
                maxWidth:"140px",
                textAlign: 'left',
                display:'flex',
                flexWrap: 'nowrap', 
                flexShrink: 0,
                overflow:'hidden'

              }}>
                {property.label}
                {property.tooltip && <Tooltip text={property.tooltip} />}
              </div>

              {/* Property Value */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                justifyContent: 'flex-end',
                minWidth:0,
                marginRight: "0px"
              }}>
                {renderInputField(property)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GenericPropertiesControl;